import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSiteStore } from "@/stores/siteStore";
import { useZoneStore } from "@/stores/zoneStore";
import { useSystemStore } from "@/stores/systemStore";
import { useAssetsStore } from "@/stores/assetsStore";
import { useEndpointStore } from "@/stores/endpointStore";
import { useRisks, useTasks, useEvidence } from "@/hooks/queries";
import { useCloudServiceStore } from "@/stores/cloudServiceStore";
import { useCloudResourceStore } from "@/stores/cloudResourceStore";
import { useInternetConnectionStore } from "@/stores/internetConnectionStore";
import { useDMZConfigStore } from "@/stores/dmzConfigStore";
import { useAuth } from "@/hooks/useAuth";
import { CriticalityBadge } from "@/components/shared/CriticalityBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  Building2,
  Network,
  Server,
  HardDrive,
  Search,
  Settings2,
  Layout,
  Cloud,
  MoreVertical,
  AlertTriangle,
  AlertCircle,
  ClipboardCheck,
  Users,
  FileText,
  Download,
  Upload,
  Box,
  Globe,
  Shield,
  ChevronsDownUp,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
} from "lucide-react";
import { cn, getDomainIconColor } from "@/lib/utils";
import { toast } from "sonner";
import { InspectorDrawer } from "./InspectorDrawer";
import { SiteFormDialog } from "../sites/SiteFormDialog";
import { ZoneFormDialog } from "../sites/ZoneFormDialog";
import { SystemFormDialog } from "../systems/SystemFormDialog";
import { EndpointFormDialog } from "./EndpointFormDialog";
import { InternetEditDialog } from "./InternetEditDialog";
import { DMZEditDialog } from "./DMZEditDialog";
import { RiskCreateDialog } from "../risks/RiskCreateDialog";
import { ControlCreateDialog } from "../controls/ControlCreateDialog";
import { IncidentCreateDialog } from "../incidents/IncidentCreateDialog";
import { SupplierCreateDialog } from "../suppliers/SupplierCreateDialog";
import { EvidenceCreateDialog } from "../evidence/EvidenceCreateDialog";
import { CloudServiceFormDialog } from "../cloudServices/CloudServiceFormDialog";
import { CloudResourceFormDialog } from "../cloudResources/CloudResourceFormDialog";
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS } from "@/config/assetsTableColumns";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { exportAssetsToExcel, downloadExcelTemplate } from "@/utils/excelExport";
import { ExcelImportDialog } from "./ExcelImportDialog";
import { useSnapshotStore } from "@/stores/snapshotStore";
import { LinkIndicator } from "@/components/shared/LinkIndicator";

import { EntityType } from "@/types/link";

interface AssetBrowserProps {
  onViewInLayout?: (entityId: string, entityType: EntityType) => void;
  filterByOwner?: string;
}

export function AssetBrowser({ onViewInLayout, filterByOwner }: AssetBrowserProps) {
  const { sites, loadSites, deleteSite, addSite, updateSite } = useSiteStore();
  const { zones, loadZones, deleteZone, addZone, updateZone } = useZoneStore();
  const { systems, loadSystems, deleteSystem } = useSystemStore();
  const { nodes: assets, loadAssets, deleteNode, setSelectedNode } = useAssetsStore();
  const { endpoints, loadEndpoints } = useEndpointStore();
  const { cloudServices, loadCloudServices } = useCloudServiceStore();
  const { loadCloudResources, deleteCloudResource, getCloudResourcesByServiceId } = useCloudResourceStore();
  const {
    connections: internetConnections,
    loadConnections: loadInternetConnections,
    deleteConnection: deleteInternetConnection,
    getConnectionsBySiteId,
    getConnectionsByZoneId,
    getConnectionsBySystemId,
  } = useInternetConnectionStore();
  const {
    configs: dmzConfigs,
    loadConfigs: loadDMZConfigs,
    deleteConfig: deleteDMZConfig,
    getConfigsBySiteId,
    getConfigsByZoneId,
    getConfigsBySystemId,
    getConfigsByCloudServiceId,
  } = useDMZConfigStore();
  const { organization, profile } = useAuth();
  const { risks } = useRisks(organization?.id);
  const { tasks } = useTasks(organization?.id);
  const { evidence } = useEvidence(organization?.id);
  const queryClient = useQueryClient();

  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());
  const [expandedCloudServices, setExpandedCloudServices] = useState<Set<string>>(new Set());
  const [cloudServicesExpanded, setCloudServicesExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Sorting state
  const [sortConfig, setSortConfig] = useState<{ column: string | null; direction: "asc" | "desc" | null }>({
    column: null,
    direction: null,
  });

  // Entity type filter
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<string | undefined>();
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState<string | undefined>();
  const [zoneSiteId, setZoneSiteId] = useState<string>("");
  const [systemDialogOpen, setSystemDialogOpen] = useState(false);
  const [editingSystemId, setEditingSystemId] = useState<string | undefined>();
  const [systemZoneId, setSystemZoneId] = useState<string>("");
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [endpointDialogOpen, setEndpointDialogOpen] = useState(false);
  const [endpointSystemId, setEndpointSystemId] = useState<string | undefined>();
  const [endpointParentDomain, setEndpointParentDomain] = useState<"IT" | "OT" | "DMZ" | "Cloud" | undefined>();
  const [cloudServiceDialogOpen, setCloudServiceDialogOpen] = useState(false);
  const [cloudResourceDialogOpen, setCloudResourceDialogOpen] = useState(false);
  const [cloudResourceServiceId, setCloudResourceServiceId] = useState<string>("");
  const [internetDialogOpen, setInternetDialogOpen] = useState(false);
  const [editingInternetId, setEditingInternetId] = useState<string | undefined>();
  const [internetInitialData, setInternetInitialData] = useState<any>(undefined);
  const [dmzDialogOpen, setDMZDialogOpen] = useState(false);
  const [editingDMZId, setEditingDMZId] = useState<string | undefined>();
  const [dmzInitialData, setDMZInitialData] = useState<any>(undefined);

  // Quick actions state
  const [quickActionEntity, setQuickActionEntity] = useState<{ id: string; type: EntityType; name: string } | null>(
    null,
  );
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [controlDialogOpen, setControlDialogOpen] = useState(false);
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [excelImportDialogOpen, setExcelImportDialogOpen] = useState(false);

  const { addCloudService } = useCloudServiceStore();

  // Get visible columns from user preferences
  const visibleColumns = useMemo(() => {
    const savedColumns = profile?.preferences?.assets_table_columns;
    const columns = savedColumns && savedColumns.length > 0 ? savedColumns : DEFAULT_COLUMNS;
    // Filter to only columns that exist in AVAILABLE_COLUMNS (removes legacy columns like 'name')
    const validColumnKeys = AVAILABLE_COLUMNS.map((c) => c.key);
    return columns.filter((col) => validColumnKeys.includes(col));
  }, [profile?.preferences]);

  // Update column preferences
  const updateColumnPreferences = useCallback(
    async (columns: string[]) => {
      if (!profile?.user_id) return;

      const newPreferences = {
        ...profile.preferences,
        assets_table_columns: columns,
      };

      await supabase.from("profiles").update({ preferences: newPreferences }).eq("user_id", profile.user_id);

      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    [profile, queryClient],
  );

  const toggleColumn = useCallback(
    (columnKey: string) => {
      const newColumns = visibleColumns.includes(columnKey)
        ? visibleColumns.filter((k) => k !== columnKey)
        : [...visibleColumns, columnKey];
      updateColumnPreferences(newColumns);
    },
    [visibleColumns, updateColumnPreferences],
  );

  const fetchOrgMembersWithInvitations = useCallback(async () => {
    let orgMembers: Array<{ email: string; name?: string }> = [];
    if (!organization?.id) return orgMembers;

    // Get existing org members
    const { data: profiles } = await supabase.from("profiles").select("email, name").eq("org_id", organization.id);

    // Get pending invitations
    const { data: invitations } = await supabase
      .from("org_invitations")
      .select("email")
      .eq("org_id", organization.id)
      .eq("status", "pending");

    if (profiles) {
      orgMembers = profiles.map((p) => ({ email: p.email, name: p.name || undefined }));
    }
    if (invitations) {
      invitations.forEach((inv) => {
        if (!orgMembers.some((m) => m.email === inv.email)) {
          orgMembers.push({ email: inv.email, name: "(Pending Invitation)" });
        }
      });
    }
    return orgMembers;
  }, [organization?.id]);

  const handleExportExcel = useCallback(async () => {
    try {
      // Fetch org members + pending invitations for Owner dropdown
      const orgMembers = await fetchOrgMembersWithInvitations();

      // Extract all firewall rules from DMZ configs
      const allFirewallRules = dmzConfigs.flatMap((config) => config.rules || []);

      // Use endpoints directly from dedicated endpoint store (not mixed assets array)
      await exportAssetsToExcel(
        sites,
        zones,
        systems,
        endpoints,
        cloudServices,
        orgMembers,
        internetConnections,
        dmzConfigs,
        allFirewallRules,
      );
      toast.success("Assets exported to Excel");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel");
    }
  }, [
    sites,
    zones,
    systems,
    endpoints,
    cloudServices,
    internetConnections,
    dmzConfigs,
    fetchOrgMembersWithInvitations,
  ]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      // Fetch org members + pending invitations for Owner dropdown
      const orgMembers = await fetchOrgMembersWithInvitations();
      await downloadExcelTemplate(orgMembers);
      toast.success("Template downloaded");
    } catch (error) {
      console.error("Template download error:", error);
      toast.error("Failed to download template");
    }
  }, [fetchOrgMembersWithInvitations]);

  const handleImportConfirm = useCallback(
    async (preview: any): Promise<Map<string, string>> => {
      // Map temp IDs to real IDs after insertion
      const idMapping = new Map<string, string>();

      try {
        // Create pre-import snapshot
        const { createSnapshot } = useSnapshotStore.getState();
        await createSnapshot(
          `Before Excel Import - ${new Date().toLocaleString()}`,
          "Automatic backup before Excel import",
          "pre-import",
        );

        let importedCount = 0;
        let updatedCount = 0;

        // 1. Import/Update sites
        for (const site of preview.sites) {
          const { _isExisting, _existingId, _tempId, _pendingOwnerEmail, id, ...siteData } = site;
          if (_isExisting && _existingId) {
            await updateSite(_existingId, siteData);
            idMapping.set(_tempId || id, _existingId);
            updatedCount++;
          } else {
            const newSite = await addSite(siteData);
            if (_tempId && newSite?.id) {
              idMapping.set(_tempId, newSite.id);
            }
            importedCount++;
          }
        }

        // 2. Import/Update cloud services
        for (const cloudService of preview.cloudServices) {
          const { _isExisting, _existingId, _tempId, id, ...serviceData } = cloudService;
          if (_isExisting && _existingId) {
            await supabase.from("cloud_services").update(serviceData).eq("id", _existingId);
            idMapping.set(_tempId || id, _existingId);
            updatedCount++;
          } else {
            const newService = await addCloudService(serviceData);
            if (_tempId && newService?.id) {
              idMapping.set(_tempId, newService.id);
            }
            importedCount++;
          }
        }

        // 3. Import/Update zones with resolved parent IDs
        for (const zone of preview.zones) {
          const { _isExisting, _existingId, _tempId, _parentTempId, id, ...zoneData } = zone;
          if (zoneData.site_id?.startsWith("temp_")) {
            zoneData.site_id = idMapping.get(zoneData.site_id) || zoneData.site_id;
          }
          if (_isExisting && _existingId) {
            await updateZone(_existingId, zoneData);
            idMapping.set(_tempId || id, _existingId);
            updatedCount++;
          } else {
            const newZone = await addZone(zoneData);
            if (_tempId && newZone?.id) {
              idMapping.set(_tempId, newZone.id);
            }
            importedCount++;
          }
        }

        // 4. Import/Update systems with resolved parent IDs
        for (const system of preview.systems) {
          const { _isExisting, _existingId, _tempId, _parentTempId, _pendingOwnerEmail, id, ...systemData } = system;
          if (systemData.zone_id?.startsWith("temp_")) {
            systemData.zone_id = idMapping.get(systemData.zone_id) || systemData.zone_id;
          }
          if (_isExisting && _existingId) {
            await supabase.from("systems").update(systemData).eq("id", _existingId);
            idMapping.set(_tempId || id, _existingId);
            updatedCount++;
          } else {
            const { data: newSystem } = await supabase.from("systems").insert(systemData).select().single();
            if (_tempId && newSystem?.id) {
              idMapping.set(_tempId, newSystem.id);
            }
            importedCount++;
          }
        }

        // 5. Import/Update endpoints with resolved parent IDs
        for (const endpoint of preview.endpoints) {
          const { _isExisting, _existingId, _parentTempId, id, ...endpointData } = endpoint;
          if (endpointData.system_id?.startsWith("temp_")) {
            endpointData.system_id = idMapping.get(endpointData.system_id) || endpointData.system_id;
          }
          if (!endpointData.org_id && organization?.id) {
            endpointData.org_id = organization.id;
          }
          if (endpointData.system_id) {
            const parentSystem = systems.find((s) => s.id === endpointData.system_id);
            if (parentSystem?.zone_id) {
              const parentZone = zones.find((z) => z.id === parentSystem.zone_id);
              if (parentZone?.domain) {
                endpointData.category = parentZone.domain;
              }
            }
          }
          Object.keys(endpointData).forEach((key) => {
            const value = (endpointData as any)[key];
            if (value && typeof value === "object" && value._type === "undefined") {
              delete (endpointData as any)[key];
            }
          });
          if (_isExisting && _existingId) {
            await supabase.from("endpoints").update(endpointData).eq("id", _existingId);
            updatedCount++;
          } else {
            const { error: endpointError } = await supabase.from("endpoints").insert(endpointData);
            if (endpointError) {
              console.error("Endpoint import error:", endpointError, endpointData);
              continue;
            }
            importedCount++;
          }
        }

        // 6. Import Internet Connections
        for (const conn of preview.internetConnections || []) {
          const { _isExisting, _existingId, _tempId, _parentTempId, ...connData } = conn;
          if (connData.site_id?.startsWith("temp_")) connData.site_id = idMapping.get(connData.site_id);
          if (connData.zone_id?.startsWith("temp_")) connData.zone_id = idMapping.get(connData.zone_id);
          if (connData.system_id?.startsWith("temp_")) connData.system_id = idMapping.get(connData.system_id);
          if (_isExisting && _existingId) {
            await supabase.from("internet_connections").update(connData).eq("id", _existingId);
            updatedCount++;
          } else {
            const { data } = await supabase.from("internet_connections").insert(connData).select().single();
            if (_tempId && data?.id) idMapping.set(_tempId, data.id);
            importedCount++;
          }
        }

        // 7. Import DMZ Configurations
        for (const dmz of preview.dmzConfigs || []) {
          const { _isExisting, _existingId, _tempId, _parentTempId, ...dmzData } = dmz;
          if (dmzData.site_id?.startsWith("temp_")) dmzData.site_id = idMapping.get(dmzData.site_id);
          if (dmzData.zone_id?.startsWith("temp_")) dmzData.zone_id = idMapping.get(dmzData.zone_id);
          if (dmzData.system_id?.startsWith("temp_")) dmzData.system_id = idMapping.get(dmzData.system_id);
          if (dmzData.cloud_service_id?.startsWith("temp_"))
            dmzData.cloud_service_id = idMapping.get(dmzData.cloud_service_id);
          if (_isExisting && _existingId) {
            await supabase.from("dmz_configs").update(dmzData).eq("id", _existingId);
            updatedCount++;
          } else {
            const { data } = await supabase.from("dmz_configs").insert(dmzData).select().single();
            if (_tempId && data?.id) idMapping.set(_tempId, data.id);
            importedCount++;
          }
        }

        // 8. Import Firewall Rules
        for (const rule of preview.firewallRules || []) {
          const { _isExisting, _existingId, _dmzTempId, ...ruleData } = rule;
          if (ruleData.dmz_config_id?.startsWith("temp_")) {
            ruleData.dmz_config_id = idMapping.get(ruleData.dmz_config_id) || ruleData.dmz_config_id;
          }
          await supabase.from("firewall_rules").insert(ruleData);
          importedCount++;
        }

        // Reload all data
        await Promise.all([
          loadSites(),
          loadCloudServices(),
          loadZones(),
          loadSystems(),
          loadAssets(),
          loadInternetConnections(),
          loadDMZConfigs(),
        ]);

        const msg = [];
        if (importedCount > 0) msg.push(`${importedCount} new`);
        if (updatedCount > 0) msg.push(`${updatedCount} updated`);
        if (msg.length > 0) {
          toast.success(`Import complete: ${msg.join(", ")}`);
        } else {
          toast.info("No changes to import");
        }

        return idMapping;
      } catch (error) {
        console.error("Import error:", error);
        throw error;
      }
    },
    [
      addSite,
      addCloudService,
      addZone,
      updateSite,
      updateZone,
      loadSites,
      loadCloudServices,
      loadZones,
      loadSystems,
      loadAssets,
      loadInternetConnections,
      loadDMZConfigs,
    ],
  );

  useEffect(() => {
    loadSites();
    loadZones();
    loadSystems();
    loadAssets();
    loadEndpoints();
    loadCloudServices();
    loadCloudResources();
    loadInternetConnections();
    loadDMZConfigs();
  }, []);

  useEffect(() => {
    if (!systemDialogOpen) {
      loadSystems();
    }
    if (!endpointDialogOpen) {
      loadAssets();
    }
  }, [systemDialogOpen, endpointDialogOpen, loadSystems, loadAssets]);

  const toggleSite = (siteId: string) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  const toggleZone = (zoneId: string) => {
    setExpandedZones((prev) => {
      const next = new Set(prev);
      if (next.has(zoneId)) {
        next.delete(zoneId);
      } else {
        next.add(zoneId);
      }
      return next;
    });
  };

  const toggleSystem = (systemId: string) => {
    setExpandedSystems((prev) => {
      const next = new Set(prev);
      if (next.has(systemId)) {
        next.delete(systemId);
      } else {
        next.add(systemId);
      }
      return next;
    });
  };

  const toggleCloudService = (cloudServiceId: string) => {
    setExpandedCloudServices((prev) => {
      const next = new Set(prev);
      if (next.has(cloudServiceId)) {
        next.delete(cloudServiceId);
      } else {
        next.add(cloudServiceId);
      }
      return next;
    });
  };

  // Collapse/Expand All handlers
  const handleCollapseAll = () => {
    setExpandedSites(new Set());
    setExpandedZones(new Set());
    setExpandedSystems(new Set());
    setExpandedCloudServices(new Set());
    setCloudServicesExpanded(false);
  };

  const handleExpandAll = () => {
    setExpandedSites(new Set(sites.map((s) => s.id)));
    setExpandedZones(new Set(zones.map((z) => z.id)));
    setExpandedSystems(new Set(systems.map((s) => s.id)));
    setExpandedCloudServices(new Set(cloudServices.map((cs) => cs.id)));
    setCloudServicesExpanded(true);
  };

  // Sort handler
  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.column !== columnKey) return { column: columnKey, direction: "asc" };
      if (prev.direction === "asc") return { column: columnKey, direction: "desc" };
      return { column: null, direction: null };
    });
  };

  const getCounts = (entityId: string, entityType: EntityType) => {
    if (entityType === "site") {
      const siteZones = zones.filter((z) => z.site_id === entityId);
      const zoneSystems = systems.filter((s) => siteZones.some((z) => z.id === s.zone_id));
      const systemAssets = assets.filter((a) => zoneSystems.some((s) => s.id === a.data.system_id));
      return {
        risks: (risks ?? []).filter((r) => systemAssets.some((a) => a.id === r.related_to_id) && r.status !== "Closed")
          .length,
        tasks: (tasks ?? []).filter((t) => systemAssets.some((a) => a.id === t.related_to_id) && t.status !== "Done")
          .length,
        evidence: (evidence ?? []).filter((e) => systemAssets.some((a) => a.id === e.related_to_id)).length,
      };
    } else if (entityType === "zone") {
      const zoneSystems = systems.filter((s) => s.zone_id === entityId);
      const systemAssets = assets.filter((a) => zoneSystems.some((s) => s.id === a.data.system_id));
      return {
        risks: (risks ?? []).filter((r) => systemAssets.some((a) => a.id === r.related_to_id) && r.status !== "Closed")
          .length,
        tasks: (tasks ?? []).filter((t) => systemAssets.some((a) => a.id === t.related_to_id) && t.status !== "Done")
          .length,
        evidence: (evidence ?? []).filter((e) => systemAssets.some((a) => a.id === e.related_to_id)).length,
      };
    } else if (entityType === "system") {
      const systemAssets = assets.filter((a) => a.data.system_id === entityId);
      return {
        risks: (risks ?? []).filter((r) => systemAssets.some((a) => a.id === r.related_to_id) && r.status !== "Closed")
          .length,
        tasks: (tasks ?? []).filter((t) => systemAssets.some((a) => a.id === t.related_to_id) && t.status !== "Done")
          .length,
        evidence: (evidence ?? []).filter((e) => systemAssets.some((a) => a.id === e.related_to_id)).length,
      };
    } else if (entityType === "endpoint") {
      return {
        risks: (risks ?? []).filter((r) => r.related_to_id === entityId && r.status !== "Closed").length,
        tasks: (tasks ?? []).filter((t) => t.related_to_id === entityId && t.status !== "Done").length,
        evidence: (evidence ?? []).filter((e) => e.related_to_id === entityId).length,
      };
    }
    return { risks: 0, tasks: 0, evidence: 0 };
  };

  const renderCell = useCallback(
    (data: any, columnKey: string, entityType: EntityType) => {
      switch (columnKey) {
        case "type":
          if (entityType === "site") return <Badge variant="secondary">Site</Badge>;
          if (entityType === "zone") return <Badge variant="secondary">Zone</Badge>;
          if (entityType === "system") return <Badge variant="secondary">System</Badge>;
          if (entityType === "cloud") return <Badge variant="secondary">Cloud</Badge>;
          if (entityType === "cloud_resource") return <Badge variant="secondary">Resource</Badge>;
          if (entityType === "internet_connection") return <Badge variant="secondary">Internet</Badge>;
          if (entityType === "dmz_config") return <Badge variant="secondary">DMZ</Badge>;
          return <Badge variant="secondary">{data.type || data.data?.type}</Badge>;

        case "site":
          if (entityType === "site") return <span className="text-sm">{data.name}</span>;
          if (entityType === "zone") {
            const site = sites.find((s) => s.id === data.site_id);
            return <span className="text-sm">{site?.name || "-"}</span>;
          }
          if (entityType === "system") {
            const zone = zones.find((z) => z.id === data.zone_id);
            const site = sites.find((s) => s.id === zone?.site_id);
            return <span className="text-sm">{site?.name || "-"}</span>;
          }
          return <span className="text-muted-foreground">-</span>;

        case "domain":
          if (entityType === "zone") return <Badge variant="secondary">{data.domain}</Badge>;
          if (entityType === "endpoint") return <span className="text-sm">{data.data?.domain || "-"}</span>;
          return <span className="text-muted-foreground">-</span>;

        case "purdue":
          if (entityType === "zone") return <span className="text-sm">{data.purdue_level || "-"}</span>;
          if (entityType === "endpoint") return <span className="text-sm">{data.data?.purdue_level || "-"}</span>;
          return <span className="text-muted-foreground">-</span>;

        case "category":
          if (entityType === "endpoint")
            return data.data?.category ? (
              <Badge variant="secondary">{data.data.category}</Badge>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          if (entityType === "internet_connection")
            return data.connection_type ? (
              <Badge variant="secondary">{data.connection_type}</Badge>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          if (entityType === "dmz_config")
            return data.rules?.length > 0 ? (
              <Badge variant="secondary">{data.rules.length} rules</Badge>
            ) : (
              <span className="text-muted-foreground">-</span>
            );
          return <span className="text-muted-foreground">-</span>;

        case "owner":
          if (entityType === "site") return <span className="text-sm">{data.owner || "-"}</span>;
          if (entityType === "system") return <span className="text-sm">{data.owner || "-"}</span>;
          if (entityType === "internet_connection") return <span className="text-sm">{data.supplier || "-"}</span>;
          return <span className="text-muted-foreground">-</span>;

        case "criticality":
          const crit =
            entityType === "site"
              ? data.criticality
              : entityType === "zone"
                ? data.criticality
                : entityType === "cloud"
                  ? data.criticality
                  : data.data?.meta?.criticality;
          return <CriticalityBadge value={crit} />;

        case "links":
          return <LinkIndicator entityType={entityType} entityId={data.id} compact />;

        case "risks":
        case "tasks":
        case "evidence":
          const counts = getCounts(data.id, entityType);
          const count = counts[columnKey as keyof typeof counts];
          return count > 0 ? (
            <Badge variant="outline">{count}</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "provider":
          return entityType === "cloud" ? (
            <span className="text-sm">{data.provider}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "environment":
          return entityType === "cloud" && data.environment ? (
            <Badge variant="secondary">{data.environment}</Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "region":
          return entityType === "cloud" ? (
            <span className="text-sm">{data.region || "-"}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "site_code":
          return entityType === "site" && data.site_code ? (
            <code className="text-xs bg-muted px-2 py-1 rounded">{data.site_code}</code>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "address":
          return entityType === "site" && data.address ? (
            <span className="text-sm truncate max-w-xs block" title={data.address}>
              {data.address}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "country":
          return entityType === "site" && data.country ? (
            <span className="text-sm">{data.country}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "created_at":
          return data.created_at ? (
            <span className="text-sm text-muted-foreground">{new Date(data.created_at).toLocaleDateString()}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        case "updated_at":
          return data.updated_at ? (
            <span className="text-sm text-muted-foreground">{new Date(data.updated_at).toLocaleDateString()}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          );

        default:
          return <span className="text-muted-foreground">-</span>;
      }
    },
    [sites, zones, risks, tasks, evidence, getCounts],
  );

  const handleViewDetails = (id: string, _type: EntityType) => {
    setSelectedNode(id);
    setInspectorOpen(true);
  };

  const handleDelete = async (id: string, type: EntityType, name: string) => {
    if (!confirm(`Are you sure you want to delete ${name}?`)) return;

    try {
      if (type === "site") await deleteSite(id);
      else if (type === "zone") await deleteZone(id);
      else if (type === "system") await deleteSystem(id);
      else if (type === "endpoint") await deleteNode(id);
      else if (type === "cloud_resource") await deleteCloudResource(id);
      toast.success(`${name} deleted successfully`);
    } catch (error) {
      toast.error(`Failed to delete ${name}`);
      console.error(error);
    }
  };

  // Comprehensive search filtering with parent-reveal logic
  const { filteredSites, filteredCloudServices, matchedParentIds } = useMemo(() => {
    // Start with owner filter if provided
    let baseSites = filterByOwner ? sites.filter((s) => s.owner_id === filterByOwner) : sites;

    if (!searchQuery) {
      return {
        filteredSites: baseSites,
        filteredCloudServices: cloudServices,
        matchedParentIds: new Set<string>(),
      };
    }

    const query = searchQuery.toLowerCase();
    const matchedParents = new Set<string>();

    // Helper to check if string matches query
    const matches = (str: string | null | undefined) => str?.toLowerCase().includes(query) || false;

    // Find matching endpoints and track their parent hierarchy
    const matchingEndpoints = assets.filter(
      (a) =>
        matches(a.data.label as string) ||
        matches(a.data.ip as string) ||
        matches(a.data.mac as string) ||
        matches(a.data.type as string),
    );
    matchingEndpoints.forEach((endpoint) => {
      const systemId = endpoint.data.system_id as string | undefined;
      if (systemId) {
        matchedParents.add(systemId);
        const system = systems.find((s) => s.id === systemId);
        if (system?.zone_id) {
          matchedParents.add(system.zone_id);
          const zone = zones.find((z) => z.id === system.zone_id);
          if (zone?.site_id) {
            matchedParents.add(zone.site_id);
          }
        }
      }
    });

    // Find matching systems and track their parent hierarchy
    const matchingSystems = systems.filter((s) => matches(s.name));
    matchingSystems.forEach((system) => {
      matchedParents.add(system.id);
      if (system.zone_id) {
        matchedParents.add(system.zone_id);
        const zone = zones.find((z) => z.id === system.zone_id);
        if (zone?.site_id) {
          matchedParents.add(zone.site_id);
        }
      }
    });

    // Find matching zones and track their parent hierarchy
    const matchingZones = zones.filter((z) => matches(z.name) || matches(z.domain));
    matchingZones.forEach((zone) => {
      matchedParents.add(zone.id);
      if (zone.site_id) {
        matchedParents.add(zone.site_id);
      }
    });

    // Find matching cloud resources and track their parent
    cloudServices.forEach((cs) => {
      const resources = getCloudResourcesByServiceId(cs.id);
      resources.forEach((r) => {
        if (matches(r.name) || matches(r.resource_type) || matches(r.resource_id)) {
          matchedParents.add(cs.id);
        }
      });
    });

    // Filter sites: include if site matches OR any child matches
    const filteredSiteList = baseSites.filter(
      (site) => matches(site.name) || matches(site.site_code) || matches(site.address) || matchedParents.has(site.id),
    );

    // Filter cloud services: include if service matches OR any resource matches
    const filteredCloudList = cloudServices.filter(
      (cs) => matches(cs.name) || matches(cs.provider) || matchedParents.has(cs.id),
    );

    return {
      filteredSites: filteredSiteList,
      filteredCloudServices: filteredCloudList,
      matchedParentIds: matchedParents,
    };
  }, [sites, zones, systems, assets, cloudServices, searchQuery, getCloudResourcesByServiceId, filterByOwner]);

  // Auto-expand matched parents when searching
  useEffect(() => {
    if (searchQuery && matchedParentIds.size > 0) {
      // Auto-expand sites that contain matches
      const sitesToExpand = new Set<string>();
      const zonesToExpand = new Set<string>();
      const systemsToExpand = new Set<string>();
      const cloudServicesToExpand = new Set<string>();

      matchedParentIds.forEach((id) => {
        // Check if it's a site
        if (sites.some((s) => s.id === id)) {
          sitesToExpand.add(id);
        }
        // Check if it's a zone
        const zone = zones.find((z) => z.id === id);
        if (zone) {
          zonesToExpand.add(id);
          if (zone.site_id) sitesToExpand.add(zone.site_id);
        }
        // Check if it's a system
        const system = systems.find((s) => s.id === id);
        if (system) {
          systemsToExpand.add(id);
          if (system.zone_id) {
            zonesToExpand.add(system.zone_id);
            const parentZone = zones.find((z) => z.id === system.zone_id);
            if (parentZone?.site_id) sitesToExpand.add(parentZone.site_id);
          }
        }
        // Check if it's a cloud service
        if (cloudServices.some((cs) => cs.id === id)) {
          cloudServicesToExpand.add(id);
        }
      });

      if (sitesToExpand.size > 0) setExpandedSites((prev) => new Set([...prev, ...sitesToExpand]));
      if (zonesToExpand.size > 0) setExpandedZones((prev) => new Set([...prev, ...zonesToExpand]));
      if (systemsToExpand.size > 0) setExpandedSystems((prev) => new Set([...prev, ...systemsToExpand]));
      if (cloudServicesToExpand.size > 0) {
        setCloudServicesExpanded(true);
        setExpandedCloudServices((prev) => new Set([...prev, ...cloudServicesToExpand]));
      }
    }
  }, [searchQuery, matchedParentIds, sites, zones, systems, cloudServices]);

  // Generic sort helper for column sorting
  const applySortToArray = <T extends Record<string, any>>(array: T[], nameField: string = "name"): T[] => {
    if (!sortConfig.column || !sortConfig.direction) {
      return [...array].sort((a, b) => String(a[nameField] || "").localeCompare(String(b[nameField] || "")));
    }

    const { column, direction } = sortConfig;
    const multiplier = direction === "asc" ? 1 : -1;

    return [...array].sort((a, b) => {
      const aVal = a[column] ?? "";
      const bVal = b[column] ?? "";
      return String(aVal).localeCompare(String(bVal)) * multiplier;
    });
  };

  const sortedSites = useMemo(() => {
    return applySortToArray(filteredSites, "name");
  }, [filteredSites, sortConfig]);

  const getSortedZones = (siteId: string) => {
    const siteZones = zones.filter((z) => z.site_id === siteId);
    if (sortConfig.column && sortConfig.direction) {
      return applySortToArray(siteZones, "name");
    }
    const domainOrder: Record<string, number> = { IT: 0, OT: 1, DMZ: 2, Cloud: 3, External: 4, IoT: 5, Physical: 6 };
    return siteZones.sort((a, b) => {
      const orderDiff = (domainOrder[a.domain] ?? 99) - (domainOrder[b.domain] ?? 99);
      if (orderDiff !== 0) return orderDiff;
      return a.name.localeCompare(b.name);
    });
  };

  const getSortedSystems = (zoneId: string) => {
    const zoneSystems = systems.filter((s) => s.zone_id === zoneId);
    return applySortToArray(zoneSystems, "name");
  };

  const getSortedAssets = (systemId: string) => {
    const systemAssets = assets.filter((a) => a.data.system_id === systemId);
    if (sortConfig.column && sortConfig.direction) {
      const { column, direction } = sortConfig;
      const multiplier = direction === "asc" ? 1 : -1;
      return [...systemAssets].sort((a, b) => {
        const aVal = a.data[column] ?? a.data.label ?? "";
        const bVal = b.data[column] ?? b.data.label ?? "";
        return String(aVal).localeCompare(String(bVal)) * multiplier;
      });
    }
    return [...systemAssets].sort((a, b) => a.data.label.localeCompare(b.data.label));
  };

  const getCloudResourcesForService = (cloudServiceId: string) => {
    return getCloudResourcesByServiceId(cloudServiceId);
  };

  // Quick actions handlers
  const openQuickAction = (
    entityId: string,
    entityType: EntityType,
    entityName: string,
    action: "risk" | "control" | "incident" | "supplier" | "evidence",
  ) => {
    setQuickActionEntity({ id: entityId, type: entityType, name: entityName });

    switch (action) {
      case "risk":
        setRiskDialogOpen(true);
        break;
      case "control":
        setControlDialogOpen(true);
        break;
      case "incident":
        setIncidentDialogOpen(true);
        break;
      case "supplier":
        setSupplierDialogOpen(true);
        break;
      case "evidence":
        setEvidenceDialogOpen(true);
        break;
    }
  };

  // Quick Actions Dropdown Component
  const QuickActionsDropdown = ({
    entityId,
    entityType,
    entityName,
  }: {
    entityId: string;
    entityType: EntityType;
    entityName: string;
  }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-background border-border z-50">
        <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openQuickAction(entityId, entityType, entityName, "risk")}>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Link Risk
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openQuickAction(entityId, entityType, entityName, "control")}>
          <ClipboardCheck className="h-4 w-4 mr-2" />
          Link Control
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openQuickAction(entityId, entityType, entityName, "incident")}>
          <AlertCircle className="h-4 w-4 mr-2" />
          Link Incident
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openQuickAction(entityId, entityType, entityName, "supplier")}>
          <Users className="h-4 w-4 mr-2" />
          Link Supplier
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openQuickAction(entityId, entityType, entityName, "evidence")}>
          <FileText className="h-4 w-4 mr-2" />
          Link Evidence
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Empty state check
  const isEmpty = sites.length === 0 && cloudServices.length === 0;

  return (
    <div className="flex flex-col h-full relative">
      {/* Toolbar - hidden when empty */}
      {!isEmpty && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Collapse/Expand All */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={handleCollapseAll}>
                <ChevronsDownUp className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Collapse All</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={handleExpandAll}>
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Expand All</TooltipContent>
          </Tooltip>

          {/* Entity Type Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                {entityTypeFilter === "all"
                  ? "All Types"
                  : entityTypeFilter.charAt(0).toUpperCase() + entityTypeFilter.slice(1) + "s"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background border-border z-50">
              <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={entityTypeFilter === "all"}
                onCheckedChange={() => setEntityTypeFilter("all")}
              >
                All Types
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={entityTypeFilter === "site"}
                onCheckedChange={() => setEntityTypeFilter("site")}
              >
                Sites
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={entityTypeFilter === "zone"}
                onCheckedChange={() => setEntityTypeFilter("zone")}
              >
                Zones
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={entityTypeFilter === "system"}
                onCheckedChange={() => setEntityTypeFilter("system")}
              >
                Systems
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={entityTypeFilter === "endpoint"}
                onCheckedChange={() => setEntityTypeFilter("endpoint")}
              >
                Endpoints
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={entityTypeFilter === "cloud"}
                onCheckedChange={() => setEntityTypeFilter("cloud")}
              >
                Cloud Services
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background border-border z-50">
              <DropdownMenuLabel>Visible Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {AVAILABLE_COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleColumns.includes(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-background border-border z-50">
              <DropdownMenuItem onClick={handleExportExcel}>
                <Download className="h-4 w-4 mr-2" />
                Export All Assets
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadTemplate}>
                <FileText className="h-4 w-4 mr-2" />
                Download Template
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" onClick={() => setExcelImportDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import Excel
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Asset
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background border-border z-50">
              <DropdownMenuItem
                onClick={() => {
                  setEditingSiteId(undefined);
                  setSiteDialogOpen(true);
                }}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Add Site
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setCloudServiceDialogOpen(true);
                }}
              >
                <Cloud className="h-4 w-4 mr-2" />
                Add Cloud Service
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Empty State */}
      {isEmpty && (
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-background/80 backdrop-blur-sm">
          <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No assets created yet</h3>
          <p className="text-sm text-muted-foreground mb-6 text-center max-w-md">
            Start building your asset inventory by creating your first site, adding a cloud service, or importing assets
            in bulk. Tip: Download templete first time!
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button onClick={() => setSiteDialogOpen(true)}>
              <Building2 className="h-4 w-4 mr-2" />
              Create First Site
            </Button>
            <Button variant="outline" onClick={() => setCloudServiceDialogOpen(true)}>
              <Cloud className="h-4 w-4 mr-2" />
              Add Cloud Service
            </Button>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExcelImportDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import from Excel
            </Button>
          </div>
        </div>
      )}

      {/* Table Container - only show if not empty */}
      {!isEmpty && (
        <div className="flex-1 border rounded-lg overflow-auto">
          <TooltipProvider>
            <Table>
              <TableHeader className="sticky top-0 bg-background z-[1]">
                <TableRow>
                  <TableHead
                    className="w-[30%] cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      Name / Hierarchy
                      {sortConfig.column === "name" &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        ))}
                    </div>
                  </TableHead>
                  {visibleColumns.map((colKey) => {
                    const col = AVAILABLE_COLUMNS.find((c) => c.key === colKey);
                    if (!col) return null;
                    return (
                      <TableHead
                        key={colKey}
                        className={cn(col.width, col.sortable && "cursor-pointer hover:bg-muted/50 select-none")}
                        onClick={() => col.sortable && handleSort(colKey)}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {col.sortable &&
                            sortConfig.column === colKey &&
                            (sortConfig.direction === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            ))}
                        </div>
                      </TableHead>
                    );
                  })}
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Cloud Services Parent Container - FIRST in list */}
                {(entityTypeFilter === "all" || entityTypeFilter === "cloud") &&
                  (filteredCloudServices.length > 0 ? (
                    <>
                      <TableRow className="hover:bg-muted/50 bg-muted/20">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2 pl-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => setCloudServicesExpanded(!cloudServicesExpanded)}
                            >
                              {cloudServicesExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                            <Cloud className="h-4 w-4 text-purple-500" />
                            <span>Cloud Services</span>
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {filteredCloudServices.length}
                            </Badge>
                          </div>
                        </TableCell>
                        {visibleColumns.map((colKey) => (
                          <TableCell key={colKey}>
                            <span className="text-muted-foreground">-</span>
                          </TableCell>
                        ))}
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setCloudServiceDialogOpen(true)}>
                            <Plus className="h-4 w-4 mr-1" />
                            Add Service
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Individual Cloud Services */}
                      {cloudServicesExpanded &&
                        filteredCloudServices.map((cloudService) => {
                          const csExpanded = expandedCloudServices.has(cloudService.id);
                          const csResources = getCloudResourcesForService(cloudService.id);

                          return (
                            <React.Fragment key={cloudService.id}>
                              <TableRow
                                key={`cloud-${cloudService.id}`}
                                className="hover:bg-muted/50 cursor-pointer"
                                onDoubleClick={() => handleViewDetails(cloudService.id, "cloud")}
                              >
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2 pl-12">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => toggleCloudService(cloudService.id)}
                                    >
                                      {csExpanded ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight
                                          className={cn(
                                            "h-4 w-4",
                                            csResources.length === 0 && "text-muted-foreground/50",
                                          )}
                                        />
                                      )}
                                    </Button>
                                    <Cloud className={cn("h-4 w-4", getDomainIconColor("Cloud"))} />
                                    <span>{cloudService.name}</span>
                                    <Badge variant="secondary" className="ml-2 text-xs">
                                      {cloudService.provider}
                                    </Badge>
                                    <Badge variant="secondary" className="ml-2 text-xs">
                                      {csResources.length}R
                                    </Badge>
                                  </div>
                                </TableCell>
                                {visibleColumns.map((colKey) => (
                                  <TableCell key={colKey}>{renderCell(cloudService, colKey, "cloud")}</TableCell>
                                ))}
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm">
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="bg-background border-border z-50">
                                        <DropdownMenuItem
                                          onClick={() => {
                                            setCloudResourceServiceId(cloudService.id);
                                            setCloudResourceDialogOpen(true);
                                          }}
                                        >
                                          <Box className="h-4 w-4 mr-2" /> Add Resource
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => {
                                            setEditingDMZId(undefined);
                                            setDMZInitialData({ cloud_service_id: cloudService.id });
                                            setDMZDialogOpen(true);
                                          }}
                                        >
                                          <Shield className="h-4 w-4 mr-2" /> Add DMZ
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                    <Button variant="ghost" size="sm" onClick={() => setCloudServiceDialogOpen(true)}>
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    {onViewInLayout && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onViewInLayout(cloudService.id, "cloud")}
                                          >
                                            <Layout className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>View in Layout</TooltipContent>
                                      </Tooltip>
                                    )}
                                    <QuickActionsDropdown
                                      entityId={cloudService.id}
                                      entityType="cloud"
                                      entityName={cloudService.name}
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>

                              {/* Cloud Resources under Cloud Service */}
                              {csExpanded &&
                                csResources.map((resource) => (
                                  <TableRow
                                    key={`cloud-resource-${resource.id}`}
                                    className="hover:bg-muted/50 cursor-pointer"
                                    onDoubleClick={() => handleViewDetails(resource.id, "cloud_resource")}
                                  >
                                    <TableCell>
                                      <div className="flex items-center gap-2 pl-24">
                                        <Box className="h-4 w-4 text-purple-500" />
                                        <span>{resource.name}</span>
                                        <Badge variant="secondary" className="ml-2 text-xs">
                                          {resource.resource_type}
                                        </Badge>
                                      </div>
                                    </TableCell>
                                    {visibleColumns.map((colKey) => (
                                      <TableCell key={colKey}>
                                        {colKey === "type" ? (
                                          <Badge variant="secondary">Resource</Badge>
                                        ) : colKey === "criticality" ? (
                                          <CriticalityBadge value={resource.criticality} />
                                        ) : colKey === "environment" && resource.environment ? (
                                          <Badge variant="secondary">{resource.environment}</Badge>
                                        ) : colKey === "region" ? (
                                          <span className="text-sm">{resource.region || "-"}</span>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                    ))}
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDelete(resource.id, "cloud_resource", resource.name)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                        {onViewInLayout && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onViewInLayout(resource.id, "cloud_resource")}
                                              >
                                                <Layout className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>View in Layout</TooltipContent>
                                          </Tooltip>
                                        )}
                                        <QuickActionsDropdown
                                          entityId={resource.id}
                                          entityType="cloud_resource"
                                          entityName={resource.name}
                                        />
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}

                              {/* DMZ Configs under Cloud Service */}
                              {csExpanded &&
                                getConfigsByCloudServiceId(cloudService.id).map((dmz) => (
                                  <TableRow
                                    key={`dmz-cloud-${dmz.id}`}
                                    className="hover:bg-muted/50 cursor-pointer"
                                    onDoubleClick={() => handleViewDetails(dmz.id, "dmz_config")}
                                  >
                                    <TableCell>
                                      <div className="flex items-center gap-2 pl-24">
                                        <Shield className="h-4 w-4 text-red-500" />
                                        <span>{dmz.label}</span>
                                        {dmz.rules && dmz.rules.length > 0 && (
                                          <Badge variant="secondary" className="text-xs">
                                            {dmz.rules.length} rules
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    {visibleColumns.map((colKey) => (
                                      <TableCell key={colKey}>{renderCell(dmz, colKey, "dmz_config")}</TableCell>
                                    ))}
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setEditingDMZId(dmz.id);
                                            setDMZInitialData(undefined);
                                            setDMZDialogOpen(true);
                                          }}
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => deleteDMZConfig(dmz.id)}>
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                        {onViewInLayout && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => onViewInLayout(dmz.id, "dmz_config")}
                                              >
                                                <Layout className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>View in Layout</TooltipContent>
                                          </Tooltip>
                                        )}
                                        <QuickActionsDropdown
                                          entityId={dmz.id}
                                          entityType="dmz_config"
                                          entityName={dmz.label}
                                        />
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </React.Fragment>
                          );
                        })}
                    </>
                  ) : (
                    <TableRow className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2 pl-2">
                          <Cloud className="h-4 w-4 text-purple-500" />
                          <span className="text-muted-foreground">Cloud Services</span>
                        </div>
                      </TableCell>
                      {visibleColumns.map((colKey) => (
                        <TableCell key={colKey}>
                          <span className="text-muted-foreground">-</span>
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => setCloudServiceDialogOpen(true)}>
                          <Plus className="h-4 w-4 mr-1" />
                          Add First Cloud Service
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                {/* Physical Sites - show unless filtering to cloud only */}
                {entityTypeFilter !== "cloud" &&
                  sortedSites.map((site) => {
                    const siteIsExpanded = expandedSites.has(site.id);
                    const siteZones = getSortedZones(site.id);
                    const zoneCounts = siteZones.length;
                    const showZones =
                      entityTypeFilter === "all" ||
                      entityTypeFilter === "zone" ||
                      entityTypeFilter === "system" ||
                      entityTypeFilter === "endpoint";
                    const showSystems =
                      entityTypeFilter === "all" || entityTypeFilter === "system" || entityTypeFilter === "endpoint";
                    const showEndpoints = entityTypeFilter === "all" || entityTypeFilter === "endpoint";

                    return (
                      <React.Fragment key={site.id}>
                        <TableRow
                          key={site.id}
                          className="hover:bg-muted/50 cursor-pointer"
                          onDoubleClick={() => handleViewDetails(site.id, "site")}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2 pl-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSite(site.id);
                                }}
                              >
                                {siteIsExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight
                                    className={cn("h-4 w-4", zoneCounts === 0 && "text-muted-foreground/50")}
                                  />
                                )}
                              </Button>
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span>{site.name}</span>
                              <Badge variant="outline" className="ml-2 text-xs">
                                {zoneCounts}Z
                              </Badge>
                            </div>
                          </TableCell>
                          {visibleColumns.map((colKey) => (
                            <TableCell key={colKey}>{renderCell(site, colKey, "site")}</TableCell>
                          ))}
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-background border-border z-50">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setZoneSiteId(site.id);
                                      setEditingZoneId(undefined);
                                      setZoneDialogOpen(true);
                                    }}
                                  >
                                    <Network className="h-4 w-4 mr-2" /> Add Zone
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEditingInternetId(undefined);
                                      setInternetInitialData({ site_id: site.id });
                                      setInternetDialogOpen(true);
                                    }}
                                  >
                                    <Globe className="h-4 w-4 mr-2" /> Add Internet
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEditingDMZId(undefined);
                                      setDMZInitialData({ site_id: site.id });
                                      setDMZDialogOpen(true);
                                    }}
                                  >
                                    <Shield className="h-4 w-4 mr-2" /> Add DMZ
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (site.id) setEditingSiteId(site.id);
                                  setSiteDialogOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(site.id, "site", site.name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {onViewInLayout && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" onClick={() => onViewInLayout(site.id, "site")}>
                                      <Layout className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>View in Layout</TooltipContent>
                                </Tooltip>
                              )}
                              <QuickActionsDropdown entityId={site.id} entityType="site" entityName={site.name} />
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Internet Connections at Site level */}
                        {siteIsExpanded &&
                          getConnectionsBySiteId(site.id).map((conn) => (
                            <TableRow
                              key={`internet-${conn.id}`}
                              className="hover:bg-muted/50 cursor-pointer"
                              onDoubleClick={() => handleViewDetails(conn.id, "internet_connection")}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2 pl-12">
                                  <Globe className="h-4 w-4 text-blue-500" />
                                  <span>{conn.label}</span>
                                  {conn.connection_type && (
                                    <Badge variant="secondary" className="text-xs">
                                      {conn.connection_type}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              {visibleColumns.map((colKey) => (
                                <TableCell key={colKey}>{renderCell(conn, colKey, "internet_connection")}</TableCell>
                              ))}
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingInternetId(conn.id);
                                      setInternetInitialData(undefined);
                                      setInternetDialogOpen(true);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteInternetConnection(conn.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                  {onViewInLayout && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => onViewInLayout(conn.id, "internet_connection")}
                                        >
                                          <Layout className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>View in Layout</TooltipContent>
                                    </Tooltip>
                                  )}
                                  <QuickActionsDropdown
                                    entityId={conn.id}
                                    entityType="internet_connection"
                                    entityName={conn.label}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}

                        {/* DMZ Configs at Site level */}
                        {siteIsExpanded &&
                          getConfigsBySiteId(site.id).map((dmz) => (
                            <TableRow
                              key={`dmz-${dmz.id}`}
                              className="hover:bg-muted/50 cursor-pointer"
                              onDoubleClick={() => handleViewDetails(dmz.id, "dmz_config")}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2 pl-12">
                                  <Shield className="h-4 w-4 text-red-500" />
                                  <span>{dmz.label}</span>
                                  {dmz.rules && dmz.rules.length > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      {dmz.rules.length} rules
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              {visibleColumns.map((colKey) => (
                                <TableCell key={colKey}>{renderCell(dmz, colKey, "dmz_config")}</TableCell>
                              ))}
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingDMZId(dmz.id);
                                      setDMZInitialData(undefined);
                                      setDMZDialogOpen(true);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteDMZConfig(dmz.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                  {onViewInLayout && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => onViewInLayout(dmz.id, "dmz_config")}
                                        >
                                          <Layout className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>View in Layout</TooltipContent>
                                    </Tooltip>
                                  )}
                                  <QuickActionsDropdown
                                    entityId={dmz.id}
                                    entityType="dmz_config"
                                    entityName={dmz.label}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}

                        {/* Zones under Site */}
                        {siteIsExpanded &&
                          showZones &&
                          siteZones.map((zone) => {
                            const zoneIsExpanded = expandedZones.has(zone.id);
                            const zoneSystems = getSortedSystems(zone.id);

                            return (
                              <React.Fragment key={zone.id}>
                                <TableRow
                                  key={zone.id}
                                  className="hover:bg-muted/50 cursor-pointer"
                                  onDoubleClick={() => handleViewDetails(zone.id, "zone")}
                                >
                                  <TableCell>
                                    <div className="flex items-center gap-2 pl-12">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleZone(zone.id);
                                        }}
                                      >
                                        {zoneIsExpanded ? (
                                          <ChevronDown className="h-4 w-4" />
                                        ) : (
                                          <ChevronRight
                                            className={cn(
                                              "h-4 w-4",
                                              zoneSystems.length === 0 && "text-muted-foreground/50",
                                            )}
                                          />
                                        )}
                                      </Button>
                                      <Network className={cn("h-4 w-4", getDomainIconColor(zone.domain))} />
                                      <span>{zone.name}</span>
                                      <Badge variant="outline" className="ml-2 text-xs">
                                        {zoneSystems.length}S
                                      </Badge>
                                    </div>
                                  </TableCell>
                                  {visibleColumns.map((colKey) => (
                                    <TableCell key={colKey}>{renderCell(zone, colKey, "zone")}</TableCell>
                                  ))}
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                                            <Plus className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="bg-background border-border z-50">
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setEditingSystemId(undefined);
                                              setSystemZoneId(zone.id);
                                              setSystemDialogOpen(true);
                                            }}
                                          >
                                            <Server className="h-4 w-4 mr-2" /> Add System
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setEditingInternetId(undefined);
                                              setInternetInitialData({ zone_id: zone.id, site_id: zone.site_id });
                                              setInternetDialogOpen(true);
                                            }}
                                          >
                                            <Globe className="h-4 w-4 mr-2" /> Add Internet
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setEditingDMZId(undefined);
                                              setDMZInitialData({ zone_id: zone.id, site_id: zone.site_id });
                                              setDMZDialogOpen(true);
                                            }}
                                          >
                                            <Shield className="h-4 w-4 mr-2" /> Add DMZ
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingZoneId(zone.id);
                                          if (zone.site_id) setZoneSiteId(zone.site_id);
                                          setZoneDialogOpen(true);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(zone.id, "zone", zone.name)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                      {onViewInLayout && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => onViewInLayout(zone.id, "zone")}
                                            >
                                              <Layout className="h-4 w-4" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent>View in Layout</TooltipContent>
                                        </Tooltip>
                                      )}
                                      <QuickActionsDropdown
                                        entityId={zone.id}
                                        entityType="zone"
                                        entityName={zone.name}
                                      />
                                    </div>
                                  </TableCell>
                                </TableRow>

                                {/* Systems under Zone */}
                                {zoneIsExpanded &&
                                  showSystems &&
                                  zoneSystems.map((system) => {
                                    const systemIsExpanded = expandedSystems.has(system.id);
                                    const systemAssets = getSortedAssets(system.id);

                                    return (
                                      <React.Fragment key={system.id}>
                                        <TableRow
                                          key={system.id}
                                          className="hover:bg-muted/50 cursor-pointer"
                                          onDoubleClick={() => handleViewDetails(system.id, "system")}
                                        >
                                          <TableCell>
                                            <div className="flex items-center gap-2 pl-24">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleSystem(system.id);
                                                }}
                                              >
                                                {systemIsExpanded ? (
                                                  <ChevronDown className="h-4 w-4" />
                                                ) : (
                                                  <ChevronRight
                                                    className={cn(
                                                      "h-4 w-4",
                                                      systemAssets.length === 0 && "text-muted-foreground/50",
                                                    )}
                                                  />
                                                )}
                                              </Button>
                                              <Server className={cn("h-4 w-4", getDomainIconColor(zone.domain))} />
                                              <span>{system.name}</span>
                                              <Badge variant="outline" className="ml-2 text-xs">
                                                {systemAssets.length}E
                                              </Badge>
                                            </div>
                                          </TableCell>
                                          {visibleColumns.map((colKey) => (
                                            <TableCell key={colKey}>{renderCell(system, colKey, "system")}</TableCell>
                                          ))}
                                          <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                              <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                  <Button variant="ghost" size="sm">
                                                    <Plus className="h-4 w-4" />
                                                  </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                  align="end"
                                                  className="bg-background border-border z-50"
                                                >
                                                  <DropdownMenuItem
                                                    onClick={() => {
                                                      setEndpointSystemId(system.id);
                                                      setEndpointParentDomain(zone.domain as any);
                                                      setEndpointDialogOpen(true);
                                                    }}
                                                  >
                                                    <HardDrive className="h-4 w-4 mr-2" /> Add Endpoint
                                                  </DropdownMenuItem>
                                                  <DropdownMenuSeparator />
                                                  <DropdownMenuItem
                                                    onClick={() => {
                                                      setEditingInternetId(undefined);
                                                      setInternetInitialData({
                                                        system_id: system.id,
                                                        zone_id: zone.id,
                                                        site_id: zone.site_id,
                                                      });
                                                      setInternetDialogOpen(true);
                                                    }}
                                                  >
                                                    <Globe className="h-4 w-4 mr-2" /> Add Internet
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem
                                                    onClick={() => {
                                                      setEditingDMZId(undefined);
                                                      setDMZInitialData({
                                                        system_id: system.id,
                                                        zone_id: zone.id,
                                                        site_id: zone.site_id,
                                                      });
                                                      setDMZDialogOpen(true);
                                                    }}
                                                  >
                                                    <Shield className="h-4 w-4 mr-2" /> Add DMZ
                                                  </DropdownMenuItem>
                                                </DropdownMenuContent>
                                              </DropdownMenu>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditingSystemId(system.id);
                                                  setSystemZoneId(zone.id);
                                                  setSystemDialogOpen(true);
                                                }}
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(system.id, "system", system.name)}
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                              {onViewInLayout && (
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => onViewInLayout(system.id, "system")}
                                                    >
                                                      <Layout className="h-4 w-4" />
                                                    </Button>
                                                  </TooltipTrigger>
                                                  <TooltipContent>View in Layout</TooltipContent>
                                                </Tooltip>
                                              )}
                                              <QuickActionsDropdown
                                                entityId={system.id}
                                                entityType="system"
                                                entityName={system.name}
                                              />
                                            </div>
                                          </TableCell>
                                        </TableRow>

                                        {/* Endpoints under System */}
                                        {systemIsExpanded &&
                                          showEndpoints &&
                                          systemAssets.map((asset) => (
                                            <TableRow
                                              key={asset.id}
                                              className="hover:bg-muted/50 cursor-pointer"
                                              onDoubleClick={() => handleViewDetails(asset.id, "endpoint")}
                                            >
                                              <TableCell>
                                                <div className="flex items-center gap-2 pl-36">
                                                  <HardDrive
                                                    className={cn("h-4 w-4", getDomainIconColor(zone.domain))}
                                                  />
                                                  <span>{asset.data.label}</span>
                                                </div>
                                              </TableCell>
                                              {visibleColumns.map((colKey) => (
                                                <TableCell key={colKey}>
                                                  {renderCell(asset, colKey, "endpoint")}
                                                </TableCell>
                                              ))}
                                              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(asset.id, "endpoint", asset.data.label)}
                                                  >
                                                    <Trash2 className="h-4 w-4" />
                                                  </Button>
                                                  {onViewInLayout && (
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => onViewInLayout(asset.id, "endpoint")}
                                                        >
                                                          <Layout className="h-4 w-4" />
                                                        </Button>
                                                      </TooltipTrigger>
                                                      <TooltipContent>View in Layout</TooltipContent>
                                                    </Tooltip>
                                                  )}
                                                  <QuickActionsDropdown
                                                    entityId={asset.id}
                                                    entityType="endpoint"
                                                    entityName={asset.data.label}
                                                  />
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          ))}

                                        {/* Internet Connections under System */}
                                        {systemIsExpanded &&
                                          getConnectionsBySystemId(system.id).map((conn) => (
                                            <TableRow
                                              key={`internet-sys-${conn.id}`}
                                              className="hover:bg-muted/50 cursor-pointer"
                                              onDoubleClick={() => handleViewDetails(conn.id, "internet_connection")}
                                            >
                                              <TableCell>
                                                <div className="flex items-center gap-2 pl-36">
                                                  <Globe className="h-4 w-4 text-blue-500" />
                                                  <span>{conn.label}</span>
                                                  {conn.connection_type && (
                                                    <Badge variant="secondary" className="text-xs">
                                                      {conn.connection_type}
                                                    </Badge>
                                                  )}
                                                </div>
                                              </TableCell>
                                              {visibleColumns.map((colKey) => (
                                                <TableCell key={colKey}>
                                                  {renderCell(conn, colKey, "internet_connection")}
                                                </TableCell>
                                              ))}
                                              <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      setEditingInternetId(conn.id);
                                                      setInternetInitialData(undefined);
                                                      setInternetDialogOpen(true);
                                                    }}
                                                  >
                                                    <Pencil className="h-4 w-4" />
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => deleteInternetConnection(conn.id)}
                                                  >
                                                    <Trash2 className="h-4 w-4" />
                                                  </Button>
                                                  {onViewInLayout && (
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => onViewInLayout(conn.id, "internet_connection")}
                                                        >
                                                          <Layout className="h-4 w-4" />
                                                        </Button>
                                                      </TooltipTrigger>
                                                      <TooltipContent>View in Layout</TooltipContent>
                                                    </Tooltip>
                                                  )}
                                                  <QuickActionsDropdown
                                                    entityId={conn.id}
                                                    entityType="internet_connection"
                                                    entityName={conn.label}
                                                  />
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          ))}

                                        {/* DMZ Configs under System */}
                                        {systemIsExpanded &&
                                          getConfigsBySystemId(system.id).map((dmz) => (
                                            <TableRow
                                              key={`dmz-sys-${dmz.id}`}
                                              className="hover:bg-muted/50 cursor-pointer"
                                              onDoubleClick={() => handleViewDetails(dmz.id, "dmz_config")}
                                            >
                                              <TableCell>
                                                <div className="flex items-center gap-2 pl-36">
                                                  <Shield className="h-4 w-4 text-red-500" />
                                                  <span>{dmz.label}</span>
                                                  {dmz.rules && dmz.rules.length > 0 && (
                                                    <Badge variant="secondary" className="text-xs">
                                                      {dmz.rules.length} rules
                                                    </Badge>
                                                  )}
                                                </div>
                                              </TableCell>
                                              {visibleColumns.map((colKey) => (
                                                <TableCell key={colKey}>
                                                  {renderCell(dmz, colKey, "dmz_config")}
                                                </TableCell>
                                              ))}
                                              <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      setEditingDMZId(dmz.id);
                                                      setDMZInitialData(undefined);
                                                      setDMZDialogOpen(true);
                                                    }}
                                                  >
                                                    <Pencil className="h-4 w-4" />
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => deleteDMZConfig(dmz.id)}
                                                  >
                                                    <Trash2 className="h-4 w-4" />
                                                  </Button>
                                                  {onViewInLayout && (
                                                    <Tooltip>
                                                      <TooltipTrigger asChild>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => onViewInLayout(dmz.id, "dmz_config")}
                                                        >
                                                          <Layout className="h-4 w-4" />
                                                        </Button>
                                                      </TooltipTrigger>
                                                      <TooltipContent>View in Layout</TooltipContent>
                                                    </Tooltip>
                                                  )}
                                                  <QuickActionsDropdown
                                                    entityId={dmz.id}
                                                    entityType="dmz_config"
                                                    entityName={dmz.label}
                                                  />
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                      </React.Fragment>
                                    );
                                  })}

                                {/* Internet Connections under Zone */}
                                {zoneIsExpanded &&
                                  getConnectionsByZoneId(zone.id).map((conn) => (
                                    <TableRow
                                      key={`internet-zone-${conn.id}`}
                                      className="hover:bg-muted/50 cursor-pointer"
                                      onDoubleClick={() => handleViewDetails(conn.id, "internet_connection")}
                                    >
                                      <TableCell>
                                        <div className="flex items-center gap-2 pl-24">
                                          <Globe className="h-4 w-4 text-blue-500" />
                                          <span>{conn.label}</span>
                                          {conn.connection_type && (
                                            <Badge variant="secondary" className="text-xs">
                                              {conn.connection_type}
                                            </Badge>
                                          )}
                                        </div>
                                      </TableCell>
                                      {visibleColumns.map((colKey) => (
                                        <TableCell key={colKey}>
                                          {renderCell(conn, colKey, "internet_connection")}
                                        </TableCell>
                                      ))}
                                      <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setEditingInternetId(conn.id);
                                              setInternetInitialData(undefined);
                                              setInternetDialogOpen(true);
                                            }}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => deleteInternetConnection(conn.id)}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                          {onViewInLayout && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => onViewInLayout(conn.id, "internet_connection")}
                                                >
                                                  <Layout className="h-4 w-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>View in Layout</TooltipContent>
                                            </Tooltip>
                                          )}
                                          <QuickActionsDropdown
                                            entityId={conn.id}
                                            entityType="internet_connection"
                                            entityName={conn.label}
                                          />
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}

                                {/* DMZ Configs under Zone */}
                                {zoneIsExpanded &&
                                  getConfigsByZoneId(zone.id).map((dmz) => (
                                    <TableRow
                                      key={`dmz-zone-${dmz.id}`}
                                      className="hover:bg-muted/50 cursor-pointer"
                                      onDoubleClick={() => handleViewDetails(dmz.id, "dmz_config")}
                                    >
                                      <TableCell>
                                        <div className="flex items-center gap-2 pl-24">
                                          <Shield className="h-4 w-4 text-red-500" />
                                          <span>{dmz.label}</span>
                                          {dmz.rules && dmz.rules.length > 0 && (
                                            <Badge variant="secondary" className="text-xs">
                                              {dmz.rules.length} rules
                                            </Badge>
                                          )}
                                        </div>
                                      </TableCell>
                                      {visibleColumns.map((colKey) => (
                                        <TableCell key={colKey}>{renderCell(dmz, colKey, "dmz_config")}</TableCell>
                                      ))}
                                      <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setEditingDMZId(dmz.id);
                                              setDMZInitialData(undefined);
                                              setDMZDialogOpen(true);
                                            }}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={() => deleteDMZConfig(dmz.id)}>
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                          {onViewInLayout && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => onViewInLayout(dmz.id, "dmz_config")}
                                                >
                                                  <Layout className="h-4 w-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>View in Layout</TooltipContent>
                                            </Tooltip>
                                          )}
                                          <QuickActionsDropdown
                                            entityId={dmz.id}
                                            entityType="dmz_config"
                                            entityName={dmz.label}
                                          />
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </TooltipProvider>
        </div>
      )}

      <InspectorDrawer open={inspectorOpen} onOpenChange={setInspectorOpen} nodes={useAssetsStore.getState().nodes} />
      <SiteFormDialog
        open={siteDialogOpen}
        onOpenChange={(open) => {
          setSiteDialogOpen(open);
          if (!open) setEditingSiteId(undefined);
        }}
        site={editingSiteId ? sites.find((s) => s.id === editingSiteId) : undefined}
        onSubmit={async (data) => {
          if (editingSiteId) {
            await updateSite(editingSiteId, data);
          } else {
            await addSite(data);
          }
          await loadSites();
          setSiteDialogOpen(false);
          setEditingSiteId(undefined);
        }}
      />
      <ZoneFormDialog
        open={zoneDialogOpen}
        onOpenChange={setZoneDialogOpen}
        siteId={zoneSiteId}
        sites={sites.map((s) => ({ id: s.id, name: s.name }))}
        zone={editingZoneId ? zones.find((z) => z.id === editingZoneId) : undefined}
        onSubmit={async (zoneData) => {
          if (editingZoneId) {
            await updateZone(editingZoneId, zoneData as any);
          } else {
            await addZone(zoneData as any);
          }
          await loadZones();
          setZoneDialogOpen(false);
          setZoneSiteId("");
          setEditingZoneId(undefined);
        }}
      />
      <CloudResourceFormDialog
        open={cloudResourceDialogOpen}
        onOpenChange={setCloudResourceDialogOpen}
        cloudServiceId={cloudResourceServiceId}
        onSuccess={() => {
          loadCloudResources();
          setCloudResourceDialogOpen(false);
        }}
      />
      <SystemFormDialog
        open={systemDialogOpen}
        onOpenChange={setSystemDialogOpen}
        zoneId={systemZoneId}
        system={editingSystemId ? systems.find((s) => s.id === editingSystemId) : undefined}
      />
      <EndpointFormDialog
        open={endpointDialogOpen}
        onOpenChange={setEndpointDialogOpen}
        systemId={endpointSystemId}
        parentDomain={endpointParentDomain}
        onSuccess={() => {
          loadAssets();
          loadEndpoints();
          setEndpointDialogOpen(false);
        }}
      />

      {/* Quick Action Dialogs */}
      {quickActionEntity && (
        <>
          <RiskCreateDialog
            open={riskDialogOpen}
            onOpenChange={setRiskDialogOpen}
            preLinkedEntity={quickActionEntity}
            onSave={async () => {
              toast.success(`Risk linked to ${quickActionEntity.name}`);
              setRiskDialogOpen(false);
              setQuickActionEntity(null);
            }}
          />
          <ControlCreateDialog
            open={controlDialogOpen}
            onOpenChange={setControlDialogOpen}
            preLinkedEntity={quickActionEntity}
            onSave={async () => {
              toast.success(`Control linked to ${quickActionEntity.name}`);
              setControlDialogOpen(false);
              setQuickActionEntity(null);
            }}
          />
          <IncidentCreateDialog
            open={incidentDialogOpen}
            onOpenChange={setIncidentDialogOpen}
            preLinkedEntity={quickActionEntity}
            onSave={async () => {
              toast.success(`Incident linked to ${quickActionEntity.name}`);
              setIncidentDialogOpen(false);
              setQuickActionEntity(null);
            }}
          />
          <SupplierCreateDialog
            open={supplierDialogOpen}
            onOpenChange={setSupplierDialogOpen}
            preLinkedEntity={quickActionEntity}
            onSave={async () => {
              toast.success(`Supplier linked to ${quickActionEntity.name}`);
              setSupplierDialogOpen(false);
              setQuickActionEntity(null);
            }}
          />
          <EvidenceCreateDialog
            open={evidenceDialogOpen}
            onOpenChange={setEvidenceDialogOpen}
            preLinkedEntity={quickActionEntity}
            onSave={async () => {
              toast.success(`Evidence linked to ${quickActionEntity.name}`);
              setEvidenceDialogOpen(false);
              setQuickActionEntity(null);
            }}
          />
        </>
      )}

      <CloudServiceFormDialog
        open={cloudServiceDialogOpen}
        onOpenChange={setCloudServiceDialogOpen}
        onSubmit={async (data) => {
          await addCloudService(data);
          await loadCloudServices();
          setCloudServiceDialogOpen(false);
        }}
      />

      <ExcelImportDialog
        open={excelImportDialogOpen}
        onOpenChange={setExcelImportDialogOpen}
        orgId={organization?.id || ""}
        existingAssets={{
          sites,
          cloudServices,
          zones,
          systems,
          endpoints: assets.map((a) => ({
            ...a.data,
            id: a.id,
            org_id: organization?.id || "",
            name: a.data.label,
            type: a.data.type,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })),
        }}
        onConfirm={handleImportConfirm}
      />

      {/* Internet/DMZ Dialogs */}
      <InternetEditDialog
        open={internetDialogOpen}
        onOpenChange={setInternetDialogOpen}
        connectionId={editingInternetId}
        initialData={internetInitialData}
        onSave={() => {
          loadInternetConnections();
          setInternetDialogOpen(false);
          setEditingInternetId(undefined);
        }}
      />
      <DMZEditDialog
        open={dmzDialogOpen}
        onOpenChange={setDMZDialogOpen}
        configId={editingDMZId}
        initialData={dmzInitialData}
        onSave={() => {
          loadDMZConfigs();
          setDMZDialogOpen(false);
          setEditingDMZId(undefined);
        }}
      />
    </div>
  );
}
